import { useMemo, useState } from "react";
import { decode } from "blurhash";
import { buildImageUrl, buildSrcSet } from "../utils/imageUrl";
import type { ImageVariant } from "../utils/imageUrl";

interface ImgProps {
  src: string | null | undefined;
  alt: string;
  variant: ImageVariant;
  /** Intrinsic render width in CSS pixels; used to pick a transform size. */
  renderedWidth: number;
  /** Blurhash string from the server; rendered instantly as a tiny canvas. */
  blurhash?: string | null;
  /** Intrinsic image width/height from the server. Prevents layout shift. */
  width?: number | null;
  height?: number | null;
  className?: string;
  style?: React.CSSProperties;
  loading?: "lazy" | "eager";
  onClick?: () => void;
  onError?: React.ReactEventHandler<HTMLImageElement>;
}

/**
 * Canonical image component. Pipes URLs through the Cloudflare transform
 * domain, emits responsive srcSet, and renders a blurhash placeholder while
 * the real image loads.
 */
export function Img({
  src,
  alt,
  variant,
  renderedWidth,
  blurhash,
  width,
  height,
  className,
  style,
  loading = "lazy",
  onClick,
  onError,
}: ImgProps) {
  const [loaded, setLoaded] = useState(false);

  const url = useMemo(
    () => buildImageUrl(src, variant, renderedWidth),
    [src, variant, renderedWidth],
  );
  const srcSet = useMemo(
    () => buildSrcSet(src, variant, renderedWidth),
    [src, variant, renderedWidth],
  );
  const placeholder = useMemo(() => (blurhash ? blurhashToDataUri(blurhash) : null), [blurhash]);

  if (!url) return null;

  const aspectRatio = width && height ? `${width} / ${height}` : undefined;
  const combinedStyle: React.CSSProperties = {
    aspectRatio,
    ...(placeholder && !loaded
      ? {
          backgroundImage: `url(${placeholder})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }
      : {}),
    ...style,
  };

  return (
    <img
      src={url}
      srcSet={srcSet ?? undefined}
      alt={alt}
      width={width ?? undefined}
      height={height ?? undefined}
      loading={loading}
      decoding="async"
      className={className}
      style={combinedStyle}
      onLoad={() => setLoaded(true)}
      onClick={onClick}
      onError={onError}
    />
  );
}

/**
 * Decode a blurhash into a 32x32 data URI. Called once per unique hash
 * (memoized by useMemo at the call site). 32x32 is enough visual fidelity
 * to hide behind a real image; CSS blur/scale does the rest if desired.
 */
function blurhashToDataUri(hash: string): string | null {
  try {
    const size = 32;
    const pixels = decode(hash, size, size);
    // Render onto an offscreen canvas and export as PNG data URI.
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const imageData = ctx.createImageData(size, size);
    imageData.data.set(pixels);
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL();
  } catch {
    return null;
  }
}
